import { Typography, SelectChangeEvent, Box } from '@mui/material'
import { Button } from 'Common/Supplyvue-ui'
import { MUISelect } from 'Common/Components/MUISelect'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { startOfWeek, differenceInCalendarWeeks, addWeeks } from 'date-fns'
import {
  CreatePlanDataTypes,
  SelectedVariant,
} from '../../../OperationalPlanner.types'
import { PLAN_DATA_ACTIONS } from '../../planDataReducer'

interface VariantSelectorSimpleProps {
  userSettingsData: any
  selectedTemplateKey: string | null
  setSelectedTemplateKey: (key: string | null) => void
  dispatch: React.Dispatch<any>
  createPlanData: CreatePlanDataTypes
  setHorizonsSelectionModeState: (state: any) => void
  horizonsSelectionModeState: any
  horizonSelectionMode: any
  newlyCreatedVariantName: React.MutableRefObject<string | null>
  onManageVariants: () => void
}

export const VariantSelectorSimple = ({
  userSettingsData,
  selectedTemplateKey,
  setSelectedTemplateKey,
  dispatch,
  createPlanData,
  setHorizonsSelectionModeState,
  horizonSelectionMode,
  newlyCreatedVariantName,
  onManageVariants,
}: VariantSelectorSimpleProps) => {
  const MenuItems = useMemo(() => {
    return Object.keys(userSettingsData?.createPlanUserTemplates || {}).map(
      (key: string) => ({
        label: key,
        value: key,
      })
    )
  }, [userSettingsData?.createPlanUserTemplates])

  // Handle newly created variant selection
  useEffect(() => {
    if (newlyCreatedVariantName.current !== null) {
      const timer = setTimeout(() => {
        setSelectedTemplateKey(newlyCreatedVariantName.current)
        newlyCreatedVariantName.current = null
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [
    userSettingsData?.createPlanUserTemplates,
    setSelectedTemplateKey,
    newlyCreatedVariantName,
  ])

  const calculateForHorizonAnchor = useCallback(
    (selectedTemplate: SelectedVariant) => {
      const { horizonAnchor } = selectedTemplate
      const result = { ...selectedTemplate }

      // If horizonAnchor or its content is not defined, return the original template
      if (
        !horizonAnchor ||
        !horizonAnchor.planHorizon ||
        !horizonAnchor.constrainingHorizon
      ) {
        return result
      }

      if (horizonAnchor.planHorizon === 'weeks') {
        result.planHorizonDate = addWeeks(
          createPlanData.planStartWeek,
          selectedTemplate.planHorizon
        )
      }
      if (horizonAnchor.planHorizon === 'date') {
        result.planHorizonDate = new Date(selectedTemplate.planHorizonDate as Date)
        result.planHorizon = differenceInCalendarWeeks(
          result.planHorizonDate,
          createPlanData.planStartWeek
        )
      }
      if (horizonAnchor.constrainingHorizon === 'weeks') {
        result.constrainingHorizonDate = addWeeks(
          createPlanData.planStartWeek,
          selectedTemplate.constrainingHorizon
        )
      }
      if (horizonAnchor.constrainingHorizon === 'date') {
        result.constrainingHorizonDate = new Date(selectedTemplate.constrainingHorizonDate as Date)
        result.constrainingHorizon = differenceInCalendarWeeks(
          result.constrainingHorizonDate,
          createPlanData.planStartWeek
        )
      }
      return result
    },
    [createPlanData.planStartWeek]
  )

  // Handle template selection and data loading
  useEffect(() => {
    if (!selectedTemplateKey) {
      dispatch({
        type: PLAN_DATA_ACTIONS.SET_PLAN_DATA,
        payload: createPlanData,
      })
      return
    }

    if (selectedTemplateKey && userSettingsData?.createPlanUserTemplates) {
      const selectedTemplate =
        userSettingsData.createPlanUserTemplates[selectedTemplateKey]
      if (!selectedTemplate) return

      const updatedSelectedTemplate =
        calculateForHorizonAnchor(selectedTemplate)

      // Create the payload without including the current state to avoid infinite loops
      const payload = {
        ...createPlanData, // Use the initial state as base
        ...updatedSelectedTemplate,
      }

      dispatch({
        type: PLAN_DATA_ACTIONS.SET_PLAN_DATA,
        payload,
      })

      if (updatedSelectedTemplate.horizonAnchor) {
        setHorizonsSelectionModeState({
          ...updatedSelectedTemplate.horizonAnchor,
        })
      }
    }
  }, [
    selectedTemplateKey,
    userSettingsData?.createPlanUserTemplates,
    dispatch,
    createPlanData,
    calculateForHorizonAnchor,
    setHorizonsSelectionModeState,
  ])

  const handleTemplateChange = useCallback(
    (data: SelectChangeEvent<string>) => {
      try {
        setSelectedTemplateKey(data.target.value)
        setHorizonsSelectionModeState({
          planHorizon: horizonSelectionMode.weeks,
          constrainingHorizon: horizonSelectionMode.weeks,
        })
        dispatch({
          type: PLAN_DATA_ACTIONS.SET_PLAN_START_WEEK,
          payload: startOfWeek(new Date(), {
            weekStartsOn: 1,
          }),
        })
      } catch (error) {
        console.error('Error updating template:', error)
      }
    },
    [
      setSelectedTemplateKey,
      setHorizonsSelectionModeState,
      horizonSelectionMode,
      dispatch,
    ]
  )

  return (
    <Box display="flex" flexDirection="row" justifyContent="space-between">
      <Box display="flex" alignItems="center" width="20%">
        <Typography>Variants</Typography>
      </Box>
      <Box marginLeft={3} width="300px">
        <MUISelect
          handleChange={handleTemplateChange}
          selectedTemplate={selectedTemplateKey ?? ''}
          menuData={MenuItems}
          label="Select variant"
        />
      </Box>
      <Box display="flex" alignItems="center" width="20%">
        <Button variant="outlined" onClick={onManageVariants}>
          Manage Variants
        </Button>
      </Box>
    </Box>
  )
}
